import {
  isRef, ref, unref, watch, type Ref, type UnwrapRef,
} from 'vue';
import { getAuthToken } from '@/utils/tokenManager';

export class FetchError extends Error {
  response: Response;

  constructor(response: Response, message: string) {
    super(message);
    this.response = response;
  }
}

type UnwrapArrayRefs<Tuple extends [...unknown[]]> = {
  [Index in keyof Tuple]: UnwrapRef<Tuple[Index]>;
};

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  query?: ConstructorParameters<typeof URLSearchParams>[0];
  body?: RequestInit['body'] | Record<string, unknown>;
}

export type FetchAction = <T>(
  input: RequestInfo | URL,
  init?: ApiRequestInit | undefined
) => Promise<T>;

type AbortableFn<T = unknown, Params extends Array<unknown> = []> = (
  signal: AbortSignal,
  ...params: Params
) => Promise<T>;

export interface FetchQueryOptions {
  disabled?: boolean | Ref<boolean>;
  immediate?: boolean;
  // eslint-disable-next-line no-use-before-define
  fetchAction?: FetchAction;
}

export function useAsyncData<T = unknown, Params extends Array<unknown> = []>(
  fn: (...p: UnwrapArrayRefs<Params>) => Promise<T>,
  params: Params,
  options: FetchQueryOptions = { immediate: true, disabled: false },
) {
  const data = ref<Ref<T> | null>(null);
  const loading = ref<boolean>(false);
  const requestCounter = ref(0);
  const error = ref<Ref<Error> | null>(null);

  const request = async ([disabled, ...watchedParams]: [boolean, ...UnwrapArrayRefs<Params>]) => {
    if (disabled) return;
    try {
      loading.value = true;
      requestCounter.value += 1;
      data.value = (await fn(...watchedParams)) as T;
    } catch (e: unknown) {
      error.value = e as Error;
    } finally {
      requestCounter.value -= 1;
      if (requestCounter.value === 0) {
        loading.value = false;
      }
    }
  };

  const wrapIfNotRef = params.map((param) => (isRef(param) ? param : () => param));

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  watch([() => unref(options.disabled) ?? false, ...wrapIfNotRef], request, {
    immediate: options.immediate ?? !unref(options.disabled),
    deep: true,
  });

  return {
    data,
    loading,
    error,
    refetch: (...replacedParams: UnwrapArrayRefs<Params> | []) =>
      request([
        false,
        ...((replacedParams?.length ? replacedParams : params).map((param) =>
          unref(param)) as UnwrapArrayRefs<Params>),
      ]),
  };
}

export function useAsyncAction<Params extends Array<unknown> = []>(
  fn: <T>(...p: Params) => Promise<T>,
): [typeof exec, { loading: typeof loading; error: typeof error }] {
  const loading = ref<boolean>(false);
  const error = ref<Ref<Error> | null>(null);

  const exec = async <T>(...params: Params) => {
    try {
      loading.value = true;
      const result = (await fn(...params)) as T;
      return result;
    } catch (e: unknown) {
      error.value = e as Error;
      throw e;
    } finally {
      loading.value = false;
    }
  };

  return [exec, { loading, error }];
}

export function useAbortFn<T = unknown, Params extends Array<unknown> = []>(
  fn: AbortableFn<T, UnwrapArrayRefs<Params>>,
  params: Params,
  options?: FetchQueryOptions,
) {
  const abortController = ref<Ref<AbortController> | null>(null);

  return useAsyncData(
    (...parameters: UnwrapArrayRefs<Params>) => {
      let controller = unref(abortController);
      if (controller) {
        controller.abort();
      }
      controller = new AbortController();
      abortController.value = controller;
      return fn(controller.signal, ...parameters);
    },
    params,
    options,
  );
}

export function getApiBaseUrl() {
  return window.env.VITE_API_URL || import.meta.env.VITE_API_URL;
}

export function apiUrl(path: string) {
  return `${getApiBaseUrl()}${path}`;
}

export async function fetchApiBase(
  input: RequestInfo | URL,
  init?: ApiRequestInit | undefined,
): Promise<Response> {
  const token = getAuthToken();
  let query = '';

  if (init?.query) {
    query = `?${new URLSearchParams(init.query)}`;
  }

  const headers = new Headers();

  if (init?.method && init.method.toLowerCase() !== 'GET') {
    headers.append('Content-Type', 'application/json');
  }
  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}${input}${query}`, {
      ...init,
      headers,
      body: init?.body ? JSON.stringify(init.body) : undefined,
    });

    if (!response.ok) {
      throw new FetchError(response, `Request failed with status code '${response.status}'.`);
    }

    return response;
  } catch (e: unknown) {
    if (e instanceof FetchError) {
      throw e;
    }
    throw new Error(`Request failed: ${e}`);
  }
}

export async function fetchApi<T>(
  input: RequestInfo | URL,
  init?: ApiRequestInit | undefined,
): Promise<T> {
  try {
    const response = await fetchApiBase(input, init);
    const contentType = response.headers.get('content-type');
    const isJson = contentType
      ?.split(';')
      .some((type) => ['application/json', 'application/ld+json'].includes(type.trim()));
    if (isJson) return response.json();
    return null as unknown as T;
  } catch (e: unknown) {
    if (e instanceof FetchError) {
      throw e;
    }
    throw new Error(`Request failed: ${e}`);
  }
}

type FetchParams = Parameters<typeof fetch>;

export function useFetchAction() {
  return useAsyncAction(<T>(input: FetchParams[0], init?: ApiRequestInit) =>
    fetchApi<T>(input, init));
}

export function useFetchQuery<T = unknown>(
  input: FetchParams[0] | Ref<FetchParams[0]>,
  init?: ApiRequestInit | Ref<ApiRequestInit>,
  options?: FetchQueryOptions,
) {
  const fetchAction = options?.fetchAction ?? fetchApi;

  if (!init) {
    return useAbortFn<T, [typeof input]>(
      (signal, url) => fetchAction<T>(url, { signal }),
      [input],
      options,
    );
  }

  const fnResult = useAbortFn<T, [typeof input, ApiRequestInit | Ref<ApiRequestInit> | undefined]>(
    (signal, url, internalInit) => fetchAction<T>(url, { ...internalInit, signal }),
    [input, init],
    options,
  );

  return {
    ...fnResult,
    refetch(refetchInit?: typeof init) {
      if (!refetchInit) return fnResult.refetch();
      return fnResult.refetch(unref(input), unref(refetchInit));
    },
  };
}
